import type { ReactNode } from 'react'
import { competitionRank, gapText, rankLabel } from '../shared/rank'
import type { SeasonRow } from './api'

/*
 * Navigation, from reference/design-handoff/NAVIGATION.md.
 *
 * Two components, and one rule that governs both: the badges keep the palette's
 * jobs. Gold counts something YOU still have to do; mint says something is
 * happening right now. There is no third badge and no red, because a game on
 * television is not an error and an unfinished board is not an alarm.
 */

export type Tab = 'Board' | 'This Wk' | 'Season' | 'Admin' | 'Profile'

/*
 * The icons, copied verbatim from the handoff. Stroke-only at 1.8, and every
 * stroke is `currentColor` so the active/inactive colour is set once on the
 * button and the icon simply follows - no second place to keep in step.
 */
const ICONS: Record<Tab, ReactNode> = {
  Board: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="16" height="6" stroke="currentColor" strokeWidth="1.8" />
      <rect x="3" y="13" width="16" height="6" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
  'This Wk': (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path d="M4 18V10M9 18V6M14 18V12M19 18V8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" />
    </svg>
  ),
  Season: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path d="M7 4h8v5a4 4 0 0 1-8 0V4Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 6H4v2a3 3 0 0 0 3 3M15 6h3v2a3 3 0 0 1-3 3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M11 13v3M8 18h6" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
  Admin: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="14" height="14" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 11h6M11 8v6" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
  Profile: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <circle cx="11" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 18c1-3.2 3.4-4.5 6-4.5s5 1.3 6 4.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
}

export interface TabBarProps {
  tab: Tab
  setTab: (t: Tab) => void
  isAdmin: boolean
  /** Your own unpicked games this week. The gold chip; hidden at zero. */
  unpicked: number
  /** Games in progress right now. The mint dot; hidden when nothing is live. */
  live: number
  /**
   * People waiting to be let in. Gold, on Admin, and zero for everyone else.
   *
   * Gold is the colour for a thing YOU have to do, which is exactly what an
   * unanswered request is - the same job it already does counting the picks
   * you still owe. Without it a request sat in a sub-screen of Admin that
   * nobody opens unless they already suspect it is there, which is how the
   * first person to ask to join went unanswered.
   */
  approvals: number
}

export function TabBar({ tab, setTab, isAdmin, unpicked, live, approvals }: TabBarProps) {
  const tabs: Tab[] = isAdmin
    ? ['Board', 'This Wk', 'Season', 'Admin', 'Profile']
    : ['Board', 'This Wk', 'Season', 'Profile']

  return (
    /*
     * Fixed to the viewport, with the safe-area inset added as PADDING rather
     * than as a margin - on a phone with a home indicator the bar has to reach
     * the bottom of the glass while its contents stop short of it.
     */
    <nav
      className="fixed right-0 bottom-0 left-0 z-20 mx-auto flex max-w-[520px] border-t border-hairline bg-card"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {tabs.map((t) => {
        const on = tab === t
        return (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-current={on ? 'page' : undefined}
            className={[
              'relative flex min-h-[54px] flex-1 flex-col items-center justify-center gap-[3px]',
              /* Square inset rule, not a pill and not an underline with a
                 radius - the whole direction is square-cornered. */
              on ? 'text-ink shadow-[inset_0_3px_0_var(--color-ink)]' : 'text-faint',
            ].join(' ')}
          >
            {/*
             * Both badges hang off the TAB, not off the icon.
             *
             * The spec places them at `top: 5px / 8px` with `right: 50%` and a
             * negative margin, all measured from the tab box. Anchoring to the
             * 22px icon span instead put the dot 4px high and 7px wide of its
             * mark, and the chip 3px high and 7px wide - small enough to look
             * deliberate and wrong in every screenshot.
             */}
            {t === 'Board' && unpicked > 0 && (
              <span
                className="slate-num absolute flex h-[15px] min-w-[15px] items-center justify-center rounded-[2px] bg-gold px-[3px] text-[9px] font-bold text-nightink"
                style={{ top: '5px', right: '50%', marginRight: '-18px' }}
              >
                {unpicked}
              </span>
            )}

            {t === 'Admin' && approvals > 0 && (
              <span
                className="slate-num absolute flex h-[15px] min-w-[15px] items-center justify-center rounded-[2px] bg-gold px-[3px] text-[9px] font-bold text-nightink"
                style={{ top: '5px', right: '50%', marginRight: '-18px' }}
              >
                {approvals}
              </span>
            )}

            {/* Mint says something is happening right now, and nothing else in
                the app may borrow it. */}
            {t === 'This Wk' && live > 0 && (
              <span
                className="absolute h-2 w-2 rounded-full bg-live"
                style={{ top: '8px', right: '50%', marginRight: '-16px' }}
              />
            )}

            {ICONS[t]}

            <span
              className={[
                'slate-num text-[8.5px] tracking-[0.1em] uppercase',
                on ? 'font-bold' : 'font-medium',
              ].join(' ')}
            >
              {t}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

export interface SeasonStripProps {
  rows: SeasonRow[]
  playerId: string | null
  onOpen: () => void
}

/**
 * Where you stand, on every screen except the one that is entirely about where
 * everyone stands.
 *
 * It replaces a badge on the Season tab. A badge can only say "something
 * changed"; this says what changed and by how much, which is the thing anyone
 * actually wants to know between kickoffs.
 *
 * The rank, the tie marker and the gap all come from the same functions the
 * Season table uses. Two screens disagreeing about a player's rank is how an
 * app gets quietly distrusted.
 */
export function SeasonStrip({ rows, playerId, onOpen }: SeasonStripProps) {
  if (!playerId || rows.length === 0) return null

  const i = rows.findIndex((r) => r.playerId === playerId)
  if (i === -1) return null

  const me = rows[i]!
  const totals = rows.map((r) => r.total)
  const places = competitionRank(totals)
  const place = places[i]!
  const tied = places.filter((p) => p === place).length > 1
  const leading = place === 1

  return (
    <button
      type="button"
      onClick={onOpen}
      className="slate-card mx-3 mb-2.5 flex w-[calc(100%-24px)] items-center gap-2.5 px-3 py-2 text-left"
    >
      {/* Gold ONLY when leading. Everyone else is slate: the colour that means
          achievement should not be handed out for fourth. */}
      <span
        className={[
          'shrink-0 text-[20px] leading-none font-extrabold',
          leading ? 'text-gold-text' : 'text-slate',
        ].join(' ')}
      >
        {rankLabel(place, tied)}
      </span>

      <span className="slate-num flex-1 text-[9px] tracking-[0.08em] text-mute uppercase">
        Your season &mdash; {gapText(me.total, totals)}
      </span>

      <span className="slate-num shrink-0 text-[15px] font-bold text-ink">{me.total}</span>
      <span className="slate-num shrink-0 text-[8.5px] text-faint">PTS</span>
      <span className="slate-num shrink-0 text-[10px] text-faint">&rarr;</span>
    </button>
  )
}
